import React, { useState, useEffect } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  CircularProgress,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import { Modal, TextInput, Text } from '../../Components'
import { colors } from '../../Themes'
import { getPractitionerNames, createPractitioner } from '../../Services/practitioners'

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    padding: '28px 28px 24px',
    boxSizing: 'border-box',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 24,
  },
  closeButton: {
    padding: 4,
    color: colors.primary,
  },
  infoBanner: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    backgroundColor: colors.white,
    marginBottom: 24,
  },
  infoIcon: {
    width: 20,
    height: 20,
    minWidth: 20,
    borderRadius: '50%',
    border: `1.5px solid ${colors.primary}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 12,
    fontWeight: 700,
    color: colors.primary,
    fontFamily: 'Roboto',
    lineHeight: 1,
    marginTop: 2,
  },
  fieldsWrapper: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  selectFormControl: {
    width: '100%',
    marginBottom: 0,
    marginTop: 15,
    backgroundColor: colors.background,
    borderRadius: 5,
    '& .MuiFilledInput-root': {
      backgroundColor: colors.background,
      borderRadius: 5,
      '&:hover': {
        backgroundColor: colors.backgroundHover,
      },
      '&.Mui-focused': {
        backgroundColor: colors.background,
      },
      '&:before': {
        borderBottom: 'none',
      },
      '&:after': {
        borderBottom: 'none',
      },
    },
    '& .MuiSelect-filled': {
      paddingTop: 26,
      paddingBottom: 8,
      fontSize: 16,
      fontFamily: 'Roboto',
      color: colors.primary,
    },
    '& .MuiInputLabel-filled': {
      fontSize: 14,
      color: 'hsl(0, 0%, 50%)',
      fontFamily: 'Roboto',
    },
    '& .MuiSelect-icon': {
      color: colors.primary,
    },
  },
  helperText: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    marginTop: 4,
    marginBottom: 8,
  },
  helperInfoIcon: {
    width: 16,
    height: 16,
    minWidth: 16,
    borderRadius: '50%',
    border: `1.5px solid ${colors.footnote}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 10,
    fontWeight: 700,
    color: colors.footnote,
    fontFamily: 'Roboto',
    lineHeight: 1,
    marginTop: 2,
  },
  footer: {
    display: 'flex',
    gap: 12,
    marginTop: 'auto',
    paddingTop: 24,
  },
  cancelButton: {
    flex: 1,
    textTransform: 'none',
    fontFamily: 'Roboto',
    fontSize: 15,
    fontWeight: 500,
    borderRadius: 8,
    border: `1.5px solid ${colors.borderGray}`,
    backgroundColor: colors.white,
    color: colors.primary,
    padding: '12px 0',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: colors.background,
    },
  },
  inviteButton: {
    flex: 1,
    textTransform: 'none',
    fontFamily: 'Roboto',
    fontSize: 15,
    fontWeight: 600,
    borderRadius: 8,
    backgroundColor: colors.primary,
    color: colors.white,
    padding: '12px 0',
    border: 'none',
    cursor: 'pointer',
    '&:hover:not(:disabled)': {
      backgroundColor: colors.primaryHover,
    },
    '&:disabled': {
      backgroundColor: colors.disabled,
      cursor: 'not-allowed',
    },
  },
  selectLoader: {
    color: colors.primary,
    position: 'absolute',
    right: 32,
  },
}))

function AddPractitionerDrawer({ open, onClose, onSuccess }) {
  const classes = useStyles()
  const [practitionerOptions, setPractitionerOptions] = useState([])
  const [loadingNames, setLoadingNames] = useState(false)
  const [fetchError, setFetchError] = useState(null)
  const [selectedId, setSelectedId] = useState('')
  const [username, setUsername] = useState('')
  const [email, setEmail] = useState('')
  const [inviteLoading, setInviteLoading] = useState(false)
  const [inviteError, setInviteError] = useState(null)

  useEffect(() => {
    if (!open) return
    const fetchNames = async () => {
      setLoadingNames(true)
      setFetchError(null)
      try {
        const response = await getPractitionerNames()
        const names = response?.data?.data ?? []
        setPractitionerOptions(
          names.map((p) => ({
            id: p.id,
            label: `${p.first_name} ${p.last_name}`.trim(),
          }))
        )
      } catch {
        setFetchError('Failed to load practitioners. Please try again.')
      } finally {
        setLoadingNames(false)
      }
    }
    fetchNames()
  }, [open])

  const handlePractitionerChange = (event) => {
    const id = event.target.value
    setSelectedId(id)
    const found = practitionerOptions.find((o) => o.id === id)
    if (found) {
      setUsername(found.label.replace(/\s+/g, '').toLowerCase())
    }
  }

  const handleClose = () => {
    setSelectedId('')
    setUsername('')
    setEmail('')
    setFetchError(null)
    setInviteError(null)
    onClose()
  }

  const handleInvite = async () => {
    const practitionerName = practitionerOptions.find((o) => o.id === selectedId)?.label ?? ''
    setInviteLoading(true)
    setInviteError(null)
    try {
      await createPractitioner({ practitionerName, username, email })
      handleClose()
      if (onSuccess) onSuccess()
    } catch (err) {
      const apiError = err?.response?.data?.error
      if (apiError?.name === 'EmailAlreadyInUse') {
        setInviteError('This email is already associated with another account.')
      } else if (apiError?.name === 'UserAlreadyExists') {
        setInviteError(`Username "${username}" is already taken. Please choose a different one.`)
      } else {
        setInviteError('Something went wrong. Please try again.')
      }
    } finally {
      setInviteLoading(false)
    }
  }

  return (
    <Modal opened={open} handleClose={handleClose} variant="explanation">
      <div className={classes.container}>
        <div className={classes.header}>
          <Text size="subnote" weight="bold">Add practitioner</Text>
          <IconButton className={classes.closeButton} onClick={handleClose} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>

        <div className={classes.infoBanner}>
          <div className={classes.infoIcon}>i</div>
          <Text size="footnote" color={colors.primary}>
            Once added, we&apos;ll send an email to the practitioner with their username and a temporary password so they can log in and access their dashboard.
          </Text>
        </div>

        <div className={classes.fieldsWrapper}>
          <FormControl variant="filled" className={classes.selectFormControl}>
            <InputLabel>Select practitioner</InputLabel>
            <Select
              value={selectedId}
              onChange={handlePractitionerChange}
              disableUnderline
              disabled={loadingNames}
              endAdornment={
                loadingNames
                  ? <CircularProgress size={18} className={classes.selectLoader} />
                  : undefined
              }
            >
              {fetchError ? (
                <MenuItem disabled>
                  <Text size="footnote" color={colors.secondary}>{fetchError}</Text>
                </MenuItem>
              ) : (
                practitionerOptions.map((opt) => (
                  <MenuItem key={opt.id} value={opt.id}>
                    {opt.label}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>

          <TextInput
            label="Username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />

          <div className={classes.helperText}>
            <div className={classes.helperInfoIcon}>i</div>
            <Text size="smaller" color={colors.footnote}>
              Please choose a simple username. The practitioner will use this to access their account.
            </Text>
          </div>

          <TextInput
            label="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        {inviteError && (
          <Text size="footnote" color={colors.secondary} style={{ marginTop: 8 }}>
            {inviteError}
          </Text>
        )}

        <div className={classes.footer}>
          <button className={classes.cancelButton} onClick={handleClose} disabled={inviteLoading}>
            Cancel
          </button>
          <button
            className={classes.inviteButton}
            onClick={handleInvite}
            disabled={!selectedId || !username || !email || inviteLoading}
          >
            {inviteLoading
              ? <CircularProgress size={18} style={{ color: colors.white }} />
              : 'Invite'
            }
          </button>
        </div>
      </div>
    </Modal>
  )
}

export default AddPractitionerDrawer
